<section class="space-y-5 p-4 sm:p-6" aria-label="Inventory">
  <header class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
    <div>
      <h1 class="text-2xl font-black tracking-tight">Inventory</h1>
      <p class="mt-1 text-sm text-base-content/55">Check stock and make quick updates for every store.</p>
    </div>
    <button class="btn btn-primary btn-sm rounded-xl" type="button" (click)="onAddStoreProduct()">
      <i class="fa-solid fa-plus"></i> Assign product
    </button>
  </header>

  <div class="grid grid-cols-2 gap-3 lg:grid-cols-5">
    <div class="rounded-2xl border border-base-200 bg-base-100 p-4"><p class="text-xs font-bold text-base-content/45">Total SKUs</p><p class="mt-1 text-2xl font-black">{{ totalItems() | indianCompact }}</p></div>
    <div class="rounded-2xl border border-base-200 bg-base-100 p-4"><p class="text-xs font-bold text-base-content/45">In stock</p><p class="mt-1 text-2xl font-black text-success">{{ inStockCount() | indianCompact }}</p></div>
    <div class="rounded-2xl border border-warning/25 bg-warning/5 p-4"><p class="text-xs font-bold text-base-content/45">Low stock</p><p class="mt-1 text-2xl font-black text-warning">{{ lowStock() | indianCompact }}</p></div>
    <div class="rounded-2xl border border-error/25 bg-error/5 p-4"><p class="text-xs font-bold text-base-content/45">Out of stock</p><p class="mt-1 text-2xl font-black text-error">{{ outOfStock() | indianCompact }}</p></div>
    <div class="col-span-2 rounded-2xl border border-base-200 bg-base-100 p-4 lg:col-span-1"><p class="text-xs font-bold text-base-content/45">Unavailable</p><p class="mt-1 text-2xl font-black">{{ unavailableCount() | indianCompact }}</p></div>
  </div>

  <div class="rounded-2xl border border-base-200 bg-base-100 p-4">
    <div class="grid gap-3 md:grid-cols-2 xl:grid-cols-[minmax(240px,1fr)_repeat(4,minmax(140px,auto))]">
      <label class="input input-bordered input-sm flex items-center gap-2 rounded-xl">
        <i class="fa-solid fa-search text-base-content/35"></i>
        <input class="grow" type="search" placeholder="Search product, SKU or brand" [ngModel]="searchTerm()" (ngModelChange)="searchTerm.set($event); resetPage()" />
      </label>
      @if (authService.role() !== 'store_manager') {
        <select class="select select-bordered select-sm rounded-xl" [ngModel]="selectedStore()" (ngModelChange)="selectedStore.set($event); resetPage()">
          @for (store of stores; track store) { <option [value]="store">{{ store === 'All' ? 'All stores' : store }}</option> }
        </select>
      }
      <select class="select select-bordered select-sm rounded-xl" [ngModel]="selectedBrand()" (ngModelChange)="selectedBrand.set($event); resetPage()">
        @for (brand of brands; track brand) { <option [value]="brand">{{ brand === 'All' ? 'All brands' : brand }}</option> }
      </select>
      <select class="select select-bordered select-sm rounded-xl" [ngModel]="selectedCategory()" (ngModelChange)="selectedCategory.set($event); resetPage()">
        @for (category of categories; track category) { <option [value]="category">{{ category === 'All' ? 'All categories' : category }}</option> }
      </select>
      <select class="select select-bordered select-sm rounded-xl" [ngModel]="selectedStatus()" (ngModelChange)="selectedStatus.set($event); resetPage()">
        <option value="All">All statuses</option><option value="IN STOCK">In stock</option><option value="LOW STOCK">Low stock</option><option value="OUT OF STOCK">Out of stock</option><option value="UNAVAILABLE">Unavailable</option>
      </select>
    </div>
  </div>

  <div class="overflow-hidden rounded-2xl border border-base-200 bg-base-100 shadow-sm">
    <div class="overflow-x-auto">
      <table class="table">
        <thead class="bg-base-200/50 text-xs uppercase text-base-content/50">
          <tr><th>Product</th><th>Store</th><th>Price</th><th>Stock</th><th>Status</th><th class="text-right">Action</th></tr>
        </thead>
        <tbody>
          @if (isLoading()) { <tr><td colspan="6" class="py-16 text-center"><span class="loading loading-spinner text-primary"></span></td></tr> }
          @for (item of paginatedInventory(); track item.id) {
            <tr class="border-base-200/70 hover:bg-base-200/25">
              <td><div class="flex min-w-60 items-center gap-3"><img [src]="item.image" class="h-12 w-12 rounded-xl border border-base-200 object-cover" alt=""><div><p class="font-bold">{{ item.productName }}</p><p class="text-xs text-base-content/45">{{ item.variant }} · {{ item.brand }} · {{ item.sku }}</p></div></div></td>
              <td class="text-sm">{{ item.store }}</td>
              <td><p class="font-bold">₹{{ item.sellingPrice | number:'1.0-2' }}</p>@if (item.mrp > item.sellingPrice) { <p class="text-xs text-base-content/35 line-through">₹{{ item.mrp | number:'1.0-2' }}</p> }</td>
              <td><p class="text-lg font-black">{{ item.stock }}</p><p class="text-[10px] text-base-content/40">Alert at {{ item.lowStockThreshold }}</p></td>
              <td><span class="badge badge-sm border-0 font-bold" [ngClass]="statusBadgeClass(item.status)">{{ item.status }}</span></td>
              <td class="text-right"><button class="btn btn-sm btn-outline rounded-xl" type="button" (click)="editItem(item)"><i class="fa-solid fa-pen"></i> Update</button></td>
            </tr>
          } @empty {
            @if (!isLoading()) { <tr><td colspan="6" class="py-16 text-center text-sm text-base-content/45">No inventory matches these filters.</td></tr> }
          }
        </tbody>
      </table>
    </div>
    <app-admin-paginator [totalItems]="filteredInventory().length" [currentPage]="currentPage()" [pageSize]="pageSize()" (pageChange)="currentPage.set($event)" (pageSizeChange)="changePageSize($event)" />
  </div>
</section>

@if (editorOpen() && selectedItem(); as item) {
  <div class="modal modal-open bg-black/40 backdrop-blur-sm" role="dialog" aria-modal="true">
    <div class="modal-box rounded-2xl p-6 shadow-xl max-w-md">
      <h3 class="text-lg font-black tracking-tight">Edit inventory</h3>
      <div class="mt-4 flex items-start gap-4 rounded-2xl border border-base-200 bg-base-200/30 p-4">
        <div class="flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-base-200 bg-base-100">
          @if (item.image && !editorImageFailed()) {
            <img [src]="item.image" [alt]="item.productName" (error)="editorImageFailed.set(true)" class="size-full object-contain p-1" />
          } @else {
            <i class="fa-solid fa-box text-2xl text-base-content/30" aria-hidden="true"></i>
          }
        </div>
        <div class="min-w-0 space-y-2">
          <p class="font-bold leading-snug break-words">{{ item.productName }}</p>
          <p class="text-sm font-semibold text-primary break-words">Variant: {{ item.variant }}</p>
          <p class="text-xs text-base-content/60 break-all">SKU: {{ item.sku }}</p>
          <p class="text-xs text-base-content/60 break-words">Store: {{ item.store }}</p>
        </div>
      </div>
      <p class="mt-4 text-sm">Current stock: <strong>{{ item.stock }}</strong></p>
      <app-stock-reason class="block mt-3" [disabled]="editorSaving()" (reasonChange)="editorReason.set($event)" />

      <div class="mt-4 space-y-3">
        <div class="label py-0"><span class="label-text text-xs font-bold">Mode</span></div>
        <select class="select select-sm select-bordered w-full" [ngModel]="editorMode()" (ngModelChange)="editorMode.set($event === 'add' ? 'add' : 'set')">
          <option value="add">Add stock</option>
          <option value="set">Set stock</option>
        </select>
        <div class="label py-0"><span class="label-text text-xs font-bold">Quantity</span></div>
        <input type="number" min="0" class="input input-sm input-bordered w-full" [ngModel]="editorQuantity()" (ngModelChange)="editorQuantity.set(toNumber($event))"/>
        <p class="text-xs">Resulting stock: <strong>{{ resultingStock() }}</strong></p>
        <div class="label py-0"><span class="label-text text-xs font-bold">Low stock threshold</span></div>
        <input type="number" min="0" class="input input-sm input-bordered w-full" [ngModel]="editorThreshold()" (ngModelChange)="editorThreshold.set(toNumber($event))"/>
        <label class="cursor-pointer flex items-center justify-between p-3 rounded-lg border border-base-200">
          <span class="text-sm font-bold">Available in store</span>
          <input type="checkbox" class="toggle toggle-sm toggle-success" [ngModel]="editorAvailable()" (ngModelChange)="editorAvailable.set($event)"/>
        </label>
      </div>

      <div class="mt-6 flex gap-2">
        <button class="btn btn-sm btn-ghost flex-1" (click)="editorOpen.set(false)" [disabled]="editorSaving()">Cancel</button>
        <button class="btn btn-sm btn-primary flex-1" (click)="saveEditor()" [disabled]="editorSaving()">Save</button>
      </div>
    </div>
  </div>
}
